export const META_SLUG = 'meta';

export type MetaChannel = 'catalog' | 'capi';

/** Settings of the `catalog` channel — product catalog sync towards Meta. */
export interface MetaCatalogSettings {
  catalogId: string;
  imageBaseUrl: string;
  productLinkBaseUrl: string;
  syncIntervalMinutes: number;
  graphApiVersion: string;
}

/** Settings of the `capi` channel — server-side conversion events (Conversions API). */
export interface MetaCapiSettings {
  pixelId: string;
  testEventCode: string;
}

export interface IntegrationChannel<TSettings = Record<string, unknown>> {
  channel: string;
  enabled: boolean;
  settings: TSettings;
}

export interface IntegrationResponse {
  slug: string;
  name: string;
  type: string;
  country: string | null;
  enabled: boolean;
  settings: Record<string, unknown>;
  channels: IntegrationChannel[];
  updatedAt: string;
}

export interface CatalogSyncItemError {
  externalId: string;
  message: string;
}

export interface CatalogSyncReport {
  provider: string;
  mode: 'full' | 'delta';
  startedAt: string;
  finishedAt: string;
  upserted: number;
  deleted: number;
  itemErrors?: CatalogSyncItemError[];
  /** Present when the whole run failed. */
  error?: string;
}

/** Why a channel is or is not operating, as the backend reports it to admins. */
export interface ChannelHealth {
  /** Effective state: switches + required settings + access token all present. */
  enabled: boolean;
  /** Settings keys without a usable value; empty while a switch keeps the channel off. */
  missing: string[];
  /** A token is stored but the backend cannot decrypt it: it has to be entered again. */
  tokenUnreadable: boolean;
}

export interface CatalogSyncProviderStatus extends ChannelHealth {
  lastSyncAt: string | null;
  lastReport: CatalogSyncReport | null;
}

/** Keyed by provider — today only `meta`, but the shape is multi-provider. */
export type CatalogSyncStatusResponse = Record<string, CatalogSyncProviderStatus>;

export interface TriggerSyncResponse {
  triggered: boolean;
  reason?: string;
  report?: CatalogSyncReport;
}

/** Public read-only view of what the storefront is currently receiving. */
export interface ConversionEventsConfig {
  enabled: boolean;
  pixelId: string | null;
}

/** Settings values are masked as "***" by the API; those must never be sent back. */
export const MASKED_VALUE = '***';

export const isMasked = (value: unknown): boolean => value === MASKED_VALUE;

/** Strips masked placeholders so a PUT never overwrites a real value with the mask. */
export const withoutMaskedValues = <T extends Record<string, unknown>>(settings: T): Partial<T> =>
  Object.entries(settings).reduce((acc, [key, value]) => {
    if (!isMasked(value)) acc[key as keyof T] = value as T[keyof T];
    return acc;
  }, {} as Partial<T>);

/**
 * Names of the settings the API returned masked. A channel PUT replaces the
 * settings object wholesale, so a masked key can neither be echoed back (it would
 * store the literal mask) nor omitted (it would be deleted) without losing the
 * real value — the caller has to stop and ask for it to be re-entered.
 */
export const maskedKeysOf = (settings: Record<string, unknown>): string[] =>
  Object.entries(settings)
    .filter(([, value]) => isMasked(value))
    .map(([key]) => key);

/**
 * Access tokens live encrypted in the integration's own settings, not in a
 * channel: the integration PUT merges key by key, so only a token the admin
 * typed is sent and the others keep their stored value.
 */
export const META_TOKEN_KEYS = { catalog: 'catalogAccessToken', capi: 'capiAccessToken' } as const;
export type MetaTokenKey = (typeof META_TOKEN_KEYS)[keyof typeof META_TOKEN_KEYS];

/**
 * Whether a token is stored, as far as the GET payload tells: the API returns the
 * mask for a stored token and null (or nothing) otherwise. There is no env var
 * behind it, so "missing" means the channel cannot operate until one is saved.
 */
export type MetaTokenSource = 'stored' | 'missing';

export const metaTokenSourceOf = (value: unknown): MetaTokenSource => (isMasked(value) ? 'stored' : 'missing');
